import { useState } from "react";
import Icon from "../../components/AppIcon";
import PasswordChangeForm from "../../components/profile/PasswordChangeForm";
import ProfileForm from "../../components/profile/ProfileForm";
import Button from "../../components/ui/Button";
import Loader from "../../components/ui/Loader";
import { useGetProfileQuery } from "../../store/api/authApi";

const ProfilePage = () => {
	const { data: user, isLoading, error, refetch } = useGetProfileQuery();
	const [isEditing, setIsEditing] = useState(false);
	const [showPasswordChange, setShowPasswordChange] = useState(false);

	if (isLoading) {
		return <Loader fullScreen size="md" />;
	}

	if (error) {
		return (
			<div className="flex flex-col items-center justify-center min-h-screen space-y-4">
				<Icon name="AlertCircle" size={48} className="text-destructive" />
				<h2 className="text-xl font-semibold text-foreground">
					Error Loading Profile
				</h2>
				<p className="text-muted-foreground">
					Failed to load your profile. Please try again.
				</p>
				<Button onClick={() => refetch()}>Retry</Button>
			</div>
		);
	}

	if (!user) {
		return null;
	}

	return (
		<div className="w-full max-w-4xl mx-auto p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold text-foreground">My Profile</h1>
					<p className="text-muted-foreground mt-1">
						Manage your account information and settings
					</p>
				</div>
				{!isEditing && (
					<Button
						variant="outline"
						onClick={() => setIsEditing(true)}
						iconName="Edit"
						iconPosition="left"
					>
						Edit Profile
					</Button>
				)}
			</div>

			{/* Profile Information */}
			<div className="bg-card rounded-lg border border-border p-6 space-y-6">
				{isEditing ? (
					<ProfileForm
						user={user}
						onCancel={() => setIsEditing(false)}
						onSuccess={() => {
							setIsEditing(false);
							refetch();
						}}
					/>
				) : (
					<>
						{/* Basic Information */}
						<div>
							<h3 className="text-lg font-semibold text-foreground mb-4">
								Basic Information
							</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										Full Name
									</label>
									<p className="text-lg text-foreground mt-1">
										{user.first_name} {user.last_name}
									</p>
								</div>

								<div>
									<label className="text-sm font-medium text-muted-foreground">
										Email Address
									</label>
									<p className="text-lg text-foreground mt-1">{user.email}</p>
								</div>

								<div>
									<label className="text-sm font-medium text-muted-foreground">
										Phone Number
									</label>
									<p className="text-lg text-foreground mt-1">
										{user.phone || "Not provided"}
									</p>
								</div>

								<div>
									<label className="text-sm font-medium text-muted-foreground">
										Role
									</label>
									<p className="text-lg text-foreground mt-1">
										{user.role === "tenant" && user.property_owner ? (
											<span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
												Property Owner
											</span>
										) : user.role === "tenant" ? (
											<span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full font-medium">
												Tenant
											</span>
										) : (
											<span className="capitalize">{user.role}</span>
										)}
									</p>
								</div>

								<div>
									<label className="text-sm font-medium text-muted-foreground">
										Account Status
									</label>
									<div className="flex items-center space-x-2 mt-1">
										<div
											className={`w-2 h-2 rounded-full ${
												user.is_active ? "bg-success" : "bg-error"
											}`}
										/>
										<p className="text-lg text-foreground">
											{user.is_active ? "Active" : "Inactive"}
										</p>
									</div>
								</div>

								<div>
									<label className="text-sm font-medium text-muted-foreground">
										Member Since
									</label>
									<p className="text-lg text-foreground mt-1">
										{new Date(user.created_at).toLocaleDateString()}
									</p>
								</div>
							</div>
						</div>

						{/* Notification Preferences */}
						<div className="pt-6 border-t border-border">
							<h3 className="text-lg font-semibold text-foreground mb-4">
								Notification Preferences
							</h3>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
								<div>
									<label className="text-sm font-medium text-muted-foreground">
										Email Notifications
									</label>
									<p className="text-lg text-foreground mt-1">Always Enabled</p>
									<p className="text-xs text-muted-foreground mt-1">
										Email notifications are required for important updates
									</p>
								</div>

								<div>
									<label className="text-sm font-medium text-muted-foreground">
										SMS Notifications
									</label>
									<p className="text-lg text-foreground mt-1">
										{user.sms_notifications ? "Enabled" : "Disabled"}
									</p>
								</div>
							</div>
						</div>

						{/* Tenant-specific information */}
						{user.role === "tenant" && (
							<div className="pt-6 border-t border-border">
								<h3 className="text-lg font-semibold text-foreground mb-4">
									Emergency Contact
								</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Contact Name
										</label>
										<p className="text-lg text-foreground mt-1">
											{user.emergency_contact_name || "Not provided"}
										</p>
									</div>

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Contact Phone
										</label>
										<p className="text-lg text-foreground mt-1">
											{user.emergency_contact_phone || "Not provided"}
										</p>
									</div>

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Relationship
										</label>
										<p className="text-lg text-foreground mt-1">
											{user.emergency_contact_relation || "Not provided"}
										</p>
									</div>
								</div>
							</div>
						)}

						{/* Technician-specific information */}
						{user.role === "technician" && (
							<div className="pt-6 border-t border-border">
								<h3 className="text-lg font-semibold text-foreground mb-4">
									Professional Information
								</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Primary Specialty
										</label>
										<p className="text-lg text-foreground mt-1">
											{user.primary_specialty || "Not provided"}
										</p>
									</div>

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Years of Experience
										</label>
										<p className="text-lg text-foreground mt-1">
											{user.experience || "Not provided"}
										</p>
									</div>

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											License Number
										</label>
										<p className="text-lg text-foreground mt-1">
											{user.license_number || "Not provided"}
										</p>
									</div>
								</div>
							</div>
						)}

						{/* Agent-specific information */}
						{user.role === "agent" && (
							<div className="pt-6 border-t border-border">
								<h3 className="text-lg font-semibold text-foreground mb-4">
									Business Information
								</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Brokerage Name
										</label>
										<p className="text-lg text-foreground mt-1">
											{user.brokerage_name || "Not provided"}
										</p>
									</div>

									<div className="md:col-span-2">
										<label className="text-sm font-medium text-muted-foreground">
											Service Areas
										</label>
										<p className="text-lg text-foreground mt-1">
											{user.service_areas || "Not provided"}
										</p>
									</div>
								</div>
							</div>
						)}

						{/* Admin-specific information */}
						{user.role === "admin" && (
							<div className="pt-6 border-t border-border">
								<h3 className="text-lg font-semibold text-foreground mb-4">
									Company Information
								</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Company Name
										</label>
										<p className="text-lg text-foreground mt-1">
											{user.company_name || "Not provided"}
										</p>
									</div>

									<div>
										<label className="text-sm font-medium text-muted-foreground">
											Job Title
										</label>
										<p className="text-lg text-foreground mt-1">
											{user.job_title || "Not provided"}
										</p>
									</div>
								</div>
							</div>
						)}

						{/* Actions */}
						<div className="pt-6 border-t border-border space-y-4">
							<Button
								variant="outline"
								onClick={() => setShowPasswordChange(!showPasswordChange)}
								iconName={showPasswordChange ? "ChevronUp" : "Key"}
								iconPosition="left"
								className="w-full sm:w-auto"
							>
								{showPasswordChange ? "Hide" : "Change"} Password
							</Button>

							{showPasswordChange && (
								<div className="mt-4">
									<PasswordChangeForm
										onSuccess={() => setShowPasswordChange(false)}
									/>
								</div>
							)}
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default ProfilePage;
